/* eslint-disable max-lines */
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import cn from 'classnames';
import { PencilLine } from 'lucide-react';

import PageHeader from '@components/layout/page-header.tsx';
import Loader from '@components/atoms/loaders/loader.tsx';
import DataGridV2, {
  type DataGridColumn
} from '@components/atoms/datatable/data-grid-v2';
import { GridAction } from '@components/atoms/datatable/grid-action-controller.tsx';
import { CustomerBoSubscriptionDetails } from '@components/back-office/customer/customer-subscription-details.tsx';

import {
  CustomerBoSubscription,
  useGetCustomerBoSubscriptions
} from '@utils/api/back-office/customer-bo/use-get-subscription-bo-subscriptions.tsx';
import { useDeleteCustomerBoSubscription } from '@utils/api/back-office/customer-bo/use-delete-subscription-bo.tsx';
import { useSuspendCustomerBoSubscription } from '@utils/api/back-office/customer-bo/use-suspend-subscription.tsx';
import { StatusBadge } from '@components/atoms/ui-kit/badge';
import { useGetCustomerById } from '@utils/api/back-office/customer-bo/use-get-customer-bo.tsx';
import toast from 'react-hot-toast';
import { BaseError } from '@utils/types/shared/base';
import { AxiosError } from 'axios';

type Row = {
  id: number;
  plan_id: number;
  sku: string;
  start_date: string;
  end_date: string;
  cancel_date: string;
  autoRenew: boolean;
  suspended: boolean;
  billing_channel: string;
};

export const CustomerBoSubscriptions = () => {
  const { t } = useTranslation();
  const { customerId } = useParams<{ customerId?: string }>();
  const [selectedSubscription, setSelectedSubscription] = useState<
    Row | undefined
  >();
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeletingSubscription, setIsDeletingSubscription] = useState(false);

  const numericId = Number(customerId) || 0;
  const { data, isLoading } = useGetCustomerBoSubscriptions(numericId);
  const { data: customerData } = useGetCustomerById(numericId);
  const currency = customerData?.result?.invoice_currency ?? 'EUR';

  const { mutate: deleteSubscription } = useDeleteCustomerBoSubscription(
    numericId,
    selectedSubscription?.id || 0
  );

  const { mutate: suspendSubscription } = useSuspendCustomerBoSubscription(
    numericId,
    selectedSubscription?.id || 0
  );

  const handleDelete = () => {
    setIsDeletingSubscription(true);
    deleteSubscription(undefined, {
      onSuccess: () => {
        toast.success(t('subscription-deleted-successfully'));
        setIsEditModalOpen(false);
        setIsDeletingSubscription(false);
      },
      onError: (error: AxiosError<BaseError>) => {
        const errorMessage =
          error.response?.data?.error_description ||
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (error.response?.data as any)?.detail ||
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (error.response?.data as any)?.error ||
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (error.response?.data as any)?.message ||
          t('an-error-occurred-while-deleting-the-subscription');
        toast.error(errorMessage);
        setIsDeletingSubscription(false);
      }
    });
  };

  const handleSuspend = (suspended: boolean) => {
    if (!selectedSubscription) return;

    setSelectedSubscription(prev =>
      prev ? { ...prev, suspended } : undefined
    );

    suspendSubscription(
      { suspended },
      {
        onSuccess: () => {
          toast.success(t('subscription-updated-successfully'));
        },
        onError: (error: AxiosError<BaseError>) => {
          toast.error(
            error.response?.data?.error_description ||
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              (error.response?.data as any)?.detail ||
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              (error.response?.data as any)?.error ||
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              (error.response?.data as any)?.message ||
              t('an-error-occurred-while-updating-the-subscription')
          );

          // revert optimistic toggle
          setSelectedSubscription(prev =>
            prev ? { ...prev, suspended: !suspended } : undefined
          );
        }
      }
    );

    setIsEditModalOpen(false);
  };

  const rows = useMemo<Row[]>(() => {
    if (!data) return [];
    return data.result.map((s: CustomerBoSubscription) => ({
      id: s.id,
      plan_id: s.plan_id,
      start_date: s.start_date,
      end_date: s.end_date,
      sku: s.sku,
      cancel_date: s.cancel_date,
      autoRenew: s.auto_renew,
      suspended: s.suspended,
      billing_channel: s.billing_channel
    }));
  }, [data]);

  const columns = useMemo<DataGridColumn<Row>[]>(() => {
    return [
      {
        header: t('id'),
        field: 'id',
        maxWidth: 90,
        enableSorting: false,
        enableFiltering: false,
        filterVariant: 'number'
      },
      {
        header: t('sku'),
        field: 'sku',
        minWidth: 160,
        enableSorting: false,
        enableFiltering: false,
        filterVariant: 'text',
        enableRowCopyOnClick: false
      },
      {
        header: t('subscription-start'),
        field: 'start_date',
        minWidth: 180,
        enableSorting: false,
        enableFiltering: false,
        filterVariant: 'text'
      },
      {
        header: t('subscription-end'),
        field: 'end_date',
        minWidth: 180,
        enableSorting: false,
        enableFiltering: false,
        filterVariant: 'text'
      },
      {
        header: t('auto-renew'),
        field: 'autoRenew',
        maxWidth: 130,
        enableSorting: false,
        enableFiltering: false,
        filterVariant: 'select',
        filterOptions: [
          { label: t('all'), value: '' },
          { label: t('yes'), value: 'true' },
          { label: t('no'), value: 'false' }
        ],
        cell: ({ getValue }) => {
          const isAutoRenew = getValue();
          return (
            <StatusBadge
              status={isAutoRenew ? 'active' : 'inactive'}
              label={isAutoRenew ? t('yes') : t('no')}
            />
          );
        }
      },
      {
        header: t('suspended'),
        field: 'suspended',
        maxWidth: 130,
        enableSorting: false,
        enableFiltering: false,
        filterVariant: 'select',
        filterOptions: [
          { label: t('all'), value: '' },
          { label: t('yes'), value: 'true' },
          { label: t('no'), value: 'false' }
        ],
        cell: ({ getValue }) => {
          const isSuspended = getValue();
          return (
            <StatusBadge
              status={isSuspended ? 'suspended' : 'active'}
              label={isSuspended ? t('yes') : t('no')}
            />
          );
        }
      }
    ];
  }, [t]);

  const openSubscription = (row: Row) => {
    setSelectedSubscription(row);
    setIsEditModalOpen(true);
  };

  const actions = useMemo<GridAction<Row>[]>(
    () => [
      {
        id: 'edit',
        label: t('edit'),
        icon: <PencilLine className="h-4 w-4" />,
        intent: 'primary',
        run: openSubscription
      }
    ],
    [t]
  );

  const isPast = (() => {
    const cancelDate =
      selectedSubscription?.cancel_date || selectedSubscription?.end_date;
    if (!cancelDate) return false;
    const cancelDateObj = new Date(cancelDate);
    const now = new Date();
    return cancelDateObj.setHours(0, 0, 0, 0) < now.setHours(0, 0, 0, 0);
  })();

  return (
    <div className={cn('py-2 min-w-full')}>
      {isLoading ? (
        <div className="h-screen w-full flex justify-center items-center -mt-24">
          <Loader height={10} width={3} />
        </div>
      ) : (
        <div className="space-y-4">
          <PageHeader
            title={t('customer-subscriptions')}
            contentClassName={'py-0 pb-4'}
            caption={t('list-of-all-the-subscriptions-including-their-details')}
          />
          <DataGridV2<Row>
            data={rows}
            columns={columns}
            actions={actions}
            enableRowSelection={false}
            enablePagination
            manualPagination
            totalItems={data?.total_results ?? rows.length}
            pageSize={25}
            pageSizeOptions={[25, 50, 75, 100]}
            enableSorting
            manualSorting
            enableFiltering
            manualFiltering
            showColumnFilters={false}
            filterMode="popover"
            height="75vh"
            dense
            striped
            grid={false}
            loading={isLoading}
            getRowId={row => String(row.id)}
            onRowClick={openSubscription}
          />
        </div>
      )}

      {isEditModalOpen && selectedSubscription && (
        <CustomerBoSubscriptionDetails
          subscriptionId={selectedSubscription.id}
          selectedSubscription={selectedSubscription}
          isEditModalOpen={isEditModalOpen}
          setIsEditModalOpen={setIsEditModalOpen}
          isPast={isPast}
          handleDelete={handleDelete}
          handleSuspend={handleSuspend}
          isDeletingSubscription={isDeletingSubscription}
          currency={currency}
        />
      )}
    </div>
  );
};
